import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import "../../../assets/css/Excursion.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useAllCities, cityNameToSlug } from "../search/useAllCities";
import cityPlaceholderImg from "../../../assets/img/no-image-holder.png";
import { ChevronLeftIcon } from "../../../helper/HwIconExcursion";

const ExcursionCities = () => {
    const navigate = useNavigate();
    const [selectedCountry, setSelectedCountry] = useState(null);
    const filterRef = useRef(null);
    const dragInfo = useRef({ dragged: false });
    const { cities: allCities, loading } = useAllCities();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const el = filterRef.current;
        if (!el) return;
        const onWheel = (e) => {
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                e.preventDefault();
                el.scrollBy({ left: e.deltaY });
            }
        };
        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, []);

    useEffect(() => {
        const el = filterRef.current;
        if (!el) return;
        let isDown = false;
        let startX = 0;
        let startScroll = 0;

        const onPointerDown = (e) => {
            isDown = true;
            dragInfo.current.dragged = false;
            startX = e.pageX;
            startScroll = el.scrollLeft;
        };
        const onPointerMove = (e) => {
            if (!isDown) return;
            const dx = e.pageX - startX;
            if (Math.abs(dx) > 5 && !dragInfo.current.dragged) {
                dragInfo.current.dragged = true;
                el.classList.add("dragging");
            }
            if (dragInfo.current.dragged) {
                el.scrollLeft = startScroll - dx;
            }
        };
        const onPointerUp = () => {
            isDown = false;
            el.classList.remove("dragging");
        };

        el.addEventListener("pointerdown", onPointerDown);
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp);
        return () => {
            el.removeEventListener("pointerdown", onPointerDown);
            window.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerup", onPointerUp);
        };
    }, []);

    const handleFilterKeyDown = (e) => {
        const el = filterRef.current;
        if (!el) return;
        if (e.key === "ArrowRight") {
            e.preventDefault();
            el.scrollBy({ left: 160, behavior: "smooth" });
        } else if (e.key === "ArrowLeft") {
            e.preventDefault();
            el.scrollBy({ left: -160, behavior: "smooth" });
        }
    };

    const countries = useMemo(
        () => [...new Set(allCities.map((c) => c?.countryName).filter(Boolean))],
        [allCities]
    );

    const cities = useMemo(
        () =>
            selectedCountry
                ? allCities.filter((c) => c?.countryName === selectedCountry)
                : allCities,
        [allCities, selectedCountry]
    );

    const handleCountryClick = (country, e) => {
        if (dragInfo.current.dragged) {
            dragInfo.current.dragged = false;
            return;
        }
        setSelectedCountry((prev) => (prev === country ? null : country));
        e?.currentTarget?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    };

    return (
        <div className="hw-page--width hw-cities-page">
            <div className="hw-cities-page-header hw-mt--20">
                <h1 className="hw-cities-page-title">Best Cities to Visit</h1>
                <button
                    className="hw-link hw-link--gray700 hw-link--icon"
                    onClick={() => navigate("/excursion/search")}
                >
                    <ChevronLeftIcon />
                    Back to Search
                </button>
            </div>

            <div
                className="hw-cities-filter"
                ref={filterRef}
                tabIndex={0}
                role="tablist"
                aria-label="Filter cities by country"
                onKeyDown={handleFilterKeyDown}
            >
                {loading
                    ? [1, 2, 3, 4, 5, 6].map((i) => (
                        <Skeleton key={i} height="3.6rem" width="12rem" borderRadius="0.6rem" inline />
                    ))
                    : countries.map((country) => (
                        <button
                            key={country}
                            type="button"
                            className={`hw-cities-chip${selectedCountry === country ? " active" : ""}`}
                            onClick={(e) => handleCountryClick(country, e)}
                        >
                            {country}
                        </button>
                    ))}
            </div>

            <div className="hw-cities-grid">
                {loading
                    ? [...Array(12)].map((_, i) => (
                        <div key={i} className="hw-cities-card">
                            <Skeleton height="15rem" borderRadius="0.8rem" />
                            <Skeleton height="1.8rem" width="80%" style={{ marginTop: "1.2rem" }} />
                            <Skeleton height="1.4rem" width="50%" style={{ marginTop: "0.4rem" }} />
                        </div>
                    ))
                    : cities.map((city) => (
                        <div
                            key={city.cityId}
                            className="hw-cities-card"
                            onClick={() => {
                                const params = new URLSearchParams({
                                    city: cityNameToSlug(city.cityName),
                                    "city-id": city.cityId ?? "",
                                    "country-id": city.countryId ?? "",
                                });
                                navigate(`/excursion/list?${params.toString()}`, {
                                    state: {
                                        selectedCategory: { catId: "all", catName: "All Activities" },
                                        destinationValue: city.cityId,
                                    },
                                });
                            }}
                        >
                            <div className="hw-cities-card-img-wrapper">
                                <img src={city.cityImage || cityPlaceholderImg} alt={city.cityName} className="hw-cities-card-img" />
                            </div>
                            <div className="hw-cities-card-title">Things to do in {city.cityName}</div>
                            <div className="hw-cities-card-country">{city.countryName}</div>
                        </div>
                    ))}
            </div>
        </div>
    );
};

export default ExcursionCities;
